/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import { VITE_GOOGLE_CLIENT_ID } from '../types/constants';
import { ServiceRegistry } from '../utils/ServiceRegistry';
import { StatusManager } from '../utils/StatusManager';
import { ErrorHandler } from '../utils/ErrorHandler';
import { StatusMessages } from '../utils/constants';
import type { Note, AppSettings } from '../types/types';
import type { DBService } from './DBService';

const SETTINGS_FILE_NAME = 'ConFabulator.settings.json';

/**
 * Manages all interactions with the Google Drive API.
 */
export class DriveService {
    public isGoogleAuthReady = false;
    public googleTokenClient: any | null = null;
    public currentUser: { name: string, picture: string, email: string } | null = null;
    private driveFolderId: string | null = null;
    private settingsFileId: string | null = null;
    private statusManager: StatusManager;
    
    constructor() {
        this.statusManager = new StatusManager(ServiceRegistry.get('dom'));
    }

    public setCurrentUser(user: any): void {
        this.currentUser = user;
        // Propagate to DBService for encryption
        // This would need access to DBService instance
    }

    public async init(onAuthSuccess: (tokenResponse: any) => Promise<void>): Promise<void> {
        if (!VITE_GOOGLE_CLIENT_ID || VITE_GOOGLE_CLIENT_ID.startsWith('YOUR_VITE_GOOGLE_CLIENT_ID')) {
            ErrorHandler.handle('Google Client ID configuration', new Error('Google Client ID is not configured.'), null, false);
            return;
        }

        const gapi = (window as any).gapi;
        const google = (window as any).google;

        if (!gapi || !google) {
            ErrorHandler.handle('Google API loading', new Error('Google API or GSI client not loaded.'), null, false);
            throw new Error('Google libraries failed to load. Drive functionality is unavailable.');
        }

        await new Promise<void>((resolve, reject) => {
            // First, load the GAPI client library.
            gapi.load('client', {
                callback: resolve,
                onerror: reject,
                timeout: 5000,
                ontimeout: () => reject(new Error('GAPI client load timed out.'))
            });
        });

        await gapi.client.init({
            discoveryDocs: ['https://www.googleapis.com/discovery/v1/apis/drive/v3/rest'],
        });

        this.isGoogleAuthReady = true;

        this.googleTokenClient = google.accounts.oauth2.initTokenClient({
            client_id: VITE_GOOGLE_CLIENT_ID,
            scope: 'https://www.googleapis.com/auth/drive.file',
            callback: onAuthSuccess,
            error_callback: (error: any) => {
                ErrorHandler.handle('Google Auth GSI', error, null, false);
                // Don't throw from here as it's an async callback.
                // The user may have just closed the popup.
            },
        });

        // Check for existing token and restore authentication state
        this.restoreAuthState(onAuthSuccess);
    }

    private async restoreAuthState(onAuthSuccess: (tokenResponse: any) => Promise<void>): Promise<void> {
        // First try to restore from sessionStorage
        const storedToken = this.getStoredToken();
        if (storedToken && this.isTokenValid(storedToken)) {
            (window as any).gapi.client.setToken(storedToken);
            try {
                // Verify the token is still valid by fetching user profile
                const profile = await this.getUserProfile();
                this.currentUser = profile;

                // Call the success callback with the restored token
                await onAuthSuccess(storedToken);
                ErrorHandler.log('authentication', 'Authentication state restored from stored token');
                return;
            } catch (error) {
                ErrorHandler.log('authentication', 'Stored token invalid, clearing storage');
                this.clearStoredToken();
                (window as any).gapi.client.setToken(null);
                this.currentUser = null;
            }
        }

        // Fallback to checking existing token in memory
        const existingToken = (window as any).gapi.client.getToken();
        if (existingToken && existingToken.access_token) {
            try {
                // Verify the token is still valid by fetching user profile
                const profile = await this.getUserProfile();
                this.currentUser = profile;

                // Store the valid token for future use
                this.storeToken(existingToken);

                // Call the success callback with the existing token
                await onAuthSuccess(existingToken);
                ErrorHandler.log('authentication', 'Authentication state restored from existing token');
            } catch (error) {
                ErrorHandler.log('authentication', 'Existing token invalid, will need fresh authentication');
                (window as any).gapi.client.setToken(null);
                this.currentUser = null;
            }
        }
    }

    public storeToken(tokenResponse: any): void {
        try {
            sessionStorage.setItem('google_auth_token', JSON.stringify({
                ...tokenResponse,
                stored_at: Date.now()
            }));
        } catch (error) {
            console.warn('Failed to store token in sessionStorage:', error);
        }
    }

    private getStoredToken(): any | null {
        try {
            const stored = sessionStorage.getItem('google_auth_token');
            return stored ? JSON.parse(stored) : null;
        } catch (error) {
            console.warn('Failed to retrieve stored token:', error);
            return null;
        }
    }

    private clearStoredToken(): void {
        try {
            sessionStorage.removeItem('google_auth_token');
        } catch (error) {
            console.warn('Failed to clear stored token:', error);
        }
    }

    private isTokenValid(tokenResponse: any): boolean {
        if (!tokenResponse || !tokenResponse.access_token) return false;

        // Check if token is expired (with 5 minute buffer)
        const expiresAt = (tokenResponse.stored_at || 0) + ((tokenResponse.expires_in || 3600) * 1000) - (5 * 60 * 1000);
        return Date.now() < expiresAt;
    }

    public async getUserProfile(): Promise<{ name: string, picture: string, email: string }> {
        const response = await (window as any).gapi.client.request({
            'path': 'https://www.googleapis.com/oauth2/v3/userinfo'
        });
        this.currentUser = response.result;
        return this.currentUser!;
    }

    public signOut(): void {
        const token = (window as any).gapi.client.getToken();
        if (token) {
            (window as any).google.accounts.oauth2.revoke(token.access_token, () => {
                (window as any).gapi.client.setToken(null);
                this.currentUser = null;
                this.clearStoredToken();
                ErrorHandler.log('authentication', 'User signed out and token cleared.');
            });
        } else {
            // Clear any stored token even if no current token exists
            this.clearStoredToken();
            this.currentUser = null;
        }
    }

    public async saveNote(noteContent: string, noteTitle: string, currentNote: Note): Promise<{ webViewLink: string, fileId: string }> {
        const folderId = await this.getOrCreateDriveFolder();
        
        // --- Step 1: Save the Markdown content ---
        const boundary = '-------314159265358979323846';
        const delimiter = `\r\n--${boundary}\r\n`;
        const close_delim = `\r\n--${boundary}--`;
        
        const descriptionParts = [];
        if (currentNote.category) descriptionParts.push(`Category: ${currentNote.category}`);
        if (currentNote.tags && currentNote.tags.length > 0) descriptionParts.push(`Tags: ${currentNote.tags.join(', ')}`);
        const description = descriptionParts.join('\n');

        const fileMetadata: { name: string, mimeType: string, description?: string } = { name: `${noteTitle}.md`, mimeType: 'text/markdown' };
        if (description) fileMetadata.description = description;

        let path: string, method: 'POST' | 'PATCH', requestBody: string;

        if (currentNote.driveFileId) {
            path = `/upload/drive/v3/files/${currentNote.driveFileId}?uploadType=multipart`;
            method = 'PATCH';
            requestBody = `${delimiter}Content-Type: application/json; charset=UTF-8\r\n\r\n${JSON.stringify(fileMetadata)}${delimiter}Content-Type: text/markdown\r\n\r\n${noteContent}${close_delim}`;
        } else {
            path = '/upload/drive/v3/files?uploadType=multipart';
            method = 'POST';
            const metadataWithParent = { ...fileMetadata, parents: [folderId] };
            requestBody = `${delimiter}Content-Type: application/json; charset=UTF-8\r\n\r\n${JSON.stringify(metadataWithParent)}${delimiter}Content-Type: text/markdown\r\n\r\n${noteContent}${close_delim}`;
        }

        const gapi = (window as any).gapi;
        const request = gapi.client.request({
            path,
            method,
            params: { uploadType: 'multipart' },
            headers: { 'Content-Type': `multipart/related; boundary="${boundary}"` },
            body: requestBody,
        });

        const response = await request;
        const mdFile = response.result as any;

        // --- Step 2: Save Audio Clips ---
        if (currentNote.audioClips && currentNote.audioClips.length > 0) {
            for (const [index, clip] of currentNote.audioClips.entries()) {
                await this.uploadAudioClip(clip, `${noteTitle}-audio-${index + 1}`, folderId);
            }
        }
        
        const fileDetails = await gapi.client.drive.files.get({ fileId: mdFile.id, fields: 'webViewLink' });
        return { webViewLink: fileDetails.result.webViewLink, fileId: mdFile.id };
    }

    private async uploadAudioClip(clip: Note['audioClips'][0], fileName: string, folderId: string): Promise<void> {
        const accessToken = (window as any).gapi.client.getToken().access_token;
        const extension = clip.mimeType.split('/')[1] || 'webm';
        
        const form = new FormData();
        let method: 'POST' | 'PATCH';
        let url: string;

        if (clip.driveFileId) {
            // Update existing audio file
            const metadata = { mimeType: clip.mimeType };
            form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
            form.append('file', clip.data);
            method = 'PATCH';
            url = `https://www.googleapis.com/upload/drive/v3/files/${clip.driveFileId}?uploadType=multipart`;
        } else {
            // Create new audio file
            const metadata = {
                name: `${fileName}.${extension}`,
                mimeType: clip.mimeType,
                parents: [folderId]
            };
            form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
            form.append('file', clip.data);
            method = 'POST';
            url = 'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart';
        }

        const response = await fetch(url, {
            method,
            headers: new Headers({ 'Authorization': `Bearer ${accessToken}` }),
            body: form,
        });

        if (!response.ok) {
            const errorBody = await response.text();
            throw new Error(`Failed to upload audio file: ${response.status} ${errorBody}`);
        }

        const file = await response.json();
        clip.driveFileId = file.id; // Update the clip with its new Drive ID
    }

    public async refreshNoteMetadata(driveFileId: string, dbService: DBService): Promise<void> {
        try {
            // Get the file description from Drive
            const fileResponse = await (window as any).gapi.client.drive.files.get({
                fileId: driveFileId,
                fields: 'description'
            });

            const description = fileResponse.result.description || '';
            const { category, tags } = DriveService.parseDescriptionForMetadata(description);

            // Find the local note and update its metadata
            const localNotes = await dbService.getAllNotes();
            const localNote = localNotes.find(note => note.driveFileId === driveFileId);

            if (localNote) {
                let updated = false;
                if (category && !localNote.category) {
                    localNote.category = category;
                    updated = true;
                    ErrorHandler.log('metadata refresh', `Refreshed category for note: ${category}`);
                }

                if (tags && tags.length > 0) {
                    const existingTags = localNote.tags || [];
                    const newTags = tags.filter(tag => !existingTags.includes(tag));
                    if (newTags.length > 0) {
                        localNote.tags = [...existingTags, ...newTags];
                        updated = true;
                        ErrorHandler.log('metadata refresh', `Refreshed tags for note: [${newTags.join(', ')}]`);
                    }
                }

                if (updated) {
                    await dbService.saveNote(localNote);
                    ErrorHandler.log('metadata refresh', `Updated metadata for note: ${localNote.title}`);
                }
            }
        } catch (error) {
            ErrorHandler.handle('refreshing note metadata', error, null, false);
        }
    }

    public async syncNotesMetadata(dbService: DBService): Promise<void> {
        const remoteNotes = await this.listNotesFromDrive();
        const localNotes = await dbService.getAllNotes();
        const localDriveIds = new Set(localNotes.map(n => n.driveFileId).filter(Boolean));

        for (const remoteNote of remoteNotes) {
            const description = remoteNote.description || '';
            const { category, tags } = DriveService.parseDescriptionForMetadata(description);

            ErrorHandler.log('note processing', `Processing remote note: ${remoteNote.name}`);
            ErrorHandler.log('note processing', `  Description: "${description}"`);
            ErrorHandler.log('note processing', `  Parsed category: "${category}"`);
            ErrorHandler.log('note processing', `  Parsed tags: [${tags?.join(', ') || ''}]`);

            // Check if we already have this note locally
            if (!localDriveIds.has(remoteNote.id)) {
                // Create a new metadata-only note entry (no content downloaded yet)
                const metadataNote: Note = {
                    id: `note_${new Date(remoteNote.modifiedTime).getTime()}`,
                    driveFileId: remoteNote.id,
                    title: remoteNote.name.replace(/\.md$/, ''), // Remove .md extension for title
                    rawTranscription: '',
                    polishedNote: '',
                    translatedNote: '',
                    timestamp: new Date(remoteNote.createdTime).getTime(),
                    modifiedTimestamp: new Date(remoteNote.modifiedTime).getTime(),
                    category: category || '',
                    tags: tags || [],
                    audioClips: [],
                    // Mark as metadata-only for lazy loading
                    _metadataOnly: true
                };

                ErrorHandler.log('note creation', `📝 Created new metadata note with category: "${metadataNote.category}", tags: [${metadataNote.tags?.join(', ') || ''}]`);
                await dbService.saveNote(metadataNote);
            } else {
                // Check if existing note needs metadata refresh
                const existingNote = localNotes.find(n => n.driveFileId === remoteNote.id);
                if (existingNote) {
                    let needsUpdate = false;

                    if (category && !existingNote.category) {
                        existingNote.category = category;
                        needsUpdate = true;
                        ErrorHandler.log('metadata update', `🔄 Updated existing note category: "${category}"`);
                    }

                    if (tags && tags.length > 0) {
                        const existingTags = existingNote.tags || [];
                        const newTags = tags.filter(tag => !existingTags.includes(tag));
                        if (newTags.length > 0) {
                            existingNote.tags = [...existingTags, ...newTags];
                            needsUpdate = true;
                            ErrorHandler.log('metadata update', `🔄 Added new tags to existing note: [${newTags.join(', ')}]`);
                        }
                    }

                    if (needsUpdate) {
                        await dbService.saveNote(existingNote);
                        ErrorHandler.log('metadata update', `💾 Saved updated metadata for existing note: ${existingNote.title}`);
                    }
                }
            }
        }
    }

    public static parseDescriptionForMetadata(description: string): { category?: string, tags?: string[] } {
        const result: { category?: string, tags?: string[] } = {};

        if (!description) {
            ErrorHandler.log('metadata parsing', 'No description provided for metadata parsing');
            return result;
        }

        ErrorHandler.log('metadata parsing', `Parsing description for metadata: "${description}"`);

        // Split description by newlines
        const lines = description.split('\n');

        for (const line of lines) {
            const trimmedLine = line.trim();
            ErrorHandler.log('metadata parsing', `  Processing line: "${trimmedLine}"`);

            // Look for Category: pattern
            const categoryMatch = trimmedLine.match(/^Category:\s*(.+)$/i);
            if (categoryMatch) {
                result.category = categoryMatch[1].trim();
                ErrorHandler.log('metadata parsing', `  Found category: "${result.category}"`);
            }

            // Look for Tags: pattern
            const tagsMatch = trimmedLine.match(/^Tags:\s*(.+)$/i);
            if (tagsMatch) {
                result.tags = tagsMatch[1].split(',').map(tag => tag.trim()).filter(tag => tag.length > 0);
                ErrorHandler.log('metadata parsing', `  Found tags: [${result.tags?.join(', ') || ''}]`);
            }
        }

        ErrorHandler.log('metadata parsing', `Metadata parsing complete. Category: "${result.category}", Tags: [${result.tags?.join(', ') || ''}]`);
        return result;
    }

    public async loadNoteContent(driveFileId: string): Promise<{ title: string, rawTranscription: string, polishedNote: string, translatedNote: string, timestamp: number, description?: string }> {
        const content = await this.getNoteContent(driveFileId);
        const parsedContent = DriveService.parseNoteContent(content);

        // Also get the file description for metadata
        let description = '';
        try {
            const fileResponse = await (window as any).gapi.client.drive.files.get({
                fileId: driveFileId,
                fields: 'description'
            });
            description = fileResponse.result.description || '';
        } catch (error) {
            console.warn('Could not fetch file description for metadata:', error);
        }

        return { ...parsedContent, description };
    }

    public async listNotesFromDrive(): Promise<{id: string, name: string, createdTime: string, modifiedTime: string, description?: string}[]> {
        const gapi = (window as any).gapi;

        try {
            // First, try to find notes in the ConFabulator folder
            let allFiles: any[] = [];
            let nextPageToken: string | undefined;
            let pageCount = 0;
            const maxPages = 10; // Prevent infinite loops

            // Try to get the folder ID
            let folderId: string | null = null;
            try {
                folderId = await this.getOrCreateDriveFolder();
                ErrorHandler.log('drive folder', `Using ConFabulator folder ID: ${folderId}`);
            } catch (folderError) {
                ErrorHandler.warn('drive folder', 'Could not get ConFabulator folder, searching in root');
            }

            // If we have a folder ID, search within it
            if (folderId) {
                do {
                    pageCount++;
                    ErrorHandler.log('drive listing', `Fetching Drive notes page ${pageCount} from folder...`);

                    const response = await gapi.client.drive.files.list({
                        q: `'${folderId}' in parents and mimeType='text/markdown' and trashed=false`,
                        fields: 'nextPageToken,files(id, name, createdTime, modifiedTime, description, modifiedTime)',
                        orderBy: 'modifiedTime desc',
                        pageSize: 100,
                        pageToken: nextPageToken,
                    });

                    const files = response.result.files || [];
                    allFiles = allFiles.concat(files);
                    nextPageToken = response.result.nextPageToken;

                    ErrorHandler.log('drive listing', `Found ${files.length} files in page ${pageCount}, total so far: ${allFiles.length}`);

                    // Safety check to prevent infinite loops
                    if (pageCount >= maxPages) {
                        console.warn(`Reached maximum page limit (${maxPages}), stopping pagination`);
                        break;
                    }

                } while (nextPageToken);
            }

            // Also search in root directory and other potential locations for notes that might not be in the folder
            if (allFiles.length === 0) {
                ErrorHandler.log('drive listing', 'No notes found in ConFabulator folder, searching in root directory...');
                nextPageToken = undefined;
                pageCount = 0;

                do {
                    pageCount++;
                    console.log(`Searching root directory page ${pageCount}...`);

                    const response = await gapi.client.drive.files.list({
                        q: `mimeType='text/markdown' and trashed=false and name contains='.md'`,
                        fields: 'nextPageToken,files(id, name, createdTime, modifiedTime, description, parents, modifiedTime)',
                        orderBy: 'modifiedTime desc',
                        pageSize: 100,
                        pageToken: nextPageToken,
                    });

                    const files = response.result.files || [];
                    // Filter to only include markdown files that look like notes (contain typical note content)
                    const noteFiles = files.filter(file =>
                        file.name.endsWith('.md') &&
                        (file.description || file.name.toLowerCase().includes('note') || file.name.toLowerCase().includes('transcription'))
                    );

                    allFiles = allFiles.concat(noteFiles);
                    nextPageToken = response.result.nextPageToken;

                    console.log(`Found ${noteFiles.length} potential note files in root page ${pageCount}, total so far: ${allFiles.length}`);

                    // Safety check to prevent infinite loops
                    if (pageCount >= maxPages) {
                        console.warn(`Reached maximum page limit (${maxPages}), stopping root search`);
                        break;
                    }

                } while (nextPageToken);
            }

            console.log(`Total notes found in Drive: ${allFiles.length}`);
            if (allFiles.length === 0) {
                console.log('No notes found. This could be because:');
                console.log('1. No notes have been saved to Drive yet');
                console.log('2. Notes are in a different location');
                console.log('3. There might be a permissions issue');
            }

            return allFiles;
        } catch (error) {
            console.error('Error listing notes from Drive:', error);
            throw new Error('Failed to fetch notes from Google Drive.');
        }
    }
    
    private async getNoteContent(fileId: string): Promise<string> {
        const gapi = (window as any).gapi;
        try {
            const response = await gapi.client.drive.files.get({
                fileId: fileId,
                alt: 'media',
            });
            return response.body;
        } catch (error) {
            console.error('Error getting note content:', error);
            throw new Error('Failed to retrieve note content.');
        }
    }

    public static parseNoteContent(content: string): { title: string, rawTranscription: string, polishedNote: string, translatedNote: string, timestamp: number } {
        let title = 'Untitled Note';
        let rawTranscription = '';
        let polishedNote = '';
        let translatedNote = '';
        let timestamp = Date.now();
    
        const frontmatterMatch = content.match(/^---\n([\s\S]+?)\n---\n\n/);
        if (frontmatterMatch) {
            const frontmatter = frontmatterMatch[1];
            const createdMatch = frontmatter.match(/created:\s*(.*)/);
            if (createdMatch) {
                const parsedDate = new Date(createdMatch[1]).getTime();
                if (!isNaN(parsedDate)) timestamp = parsedDate;
            }
            content = content.substring(frontmatterMatch[0].length);
        }
    
        const titleMatch = content.match(/^#\s+(.*)\n\n/);
        if (titleMatch) {
            title = titleMatch[1];
            content = content.substring(titleMatch[0].length);
        }
        
        const sections = content.split(/\n\n---\n\n/);
        sections.forEach(section => {
            if (section.startsWith('**Polished Note**')) {
                polishedNote = section.replace('**Polished Note**\n\n', '').trim();
            } else if (section.startsWith('**Raw Transcription**')) {
                rawTranscription = section.replace('**Raw Transcription**\n\n', '').trim();
            } else if (section.startsWith('**Translated Content**')) {
                translatedNote = section.replace('**Translated Content**\n\n', '').trim();
            } else if (!polishedNote && !rawTranscription && !translatedNote) {
                // If no sections are found, assume the whole content is the polished note
                polishedNote = section.trim();
            }
        });
    
        return { title, rawTranscription, polishedNote, translatedNote, timestamp };
    }

    private async getOrCreateDriveFolder(): Promise<string> {
        if (this.driveFolderId) return this.driveFolderId;
        const FOLDER_NAME = 'ConFabulator Notes';
        const gapi = (window as any).gapi;

        console.log(`Looking for folder: "${FOLDER_NAME}"`);

        const response = await gapi.client.drive.files.list({
            q: `name='${FOLDER_NAME}' and mimeType='application/vnd.google-apps.folder' and trashed=false`,
            fields: 'files(id, name)',
            spaces: 'drive',
        });

        console.log(`Folder search response:`, response.result);

        if (response.result.files && response.result.files.length > 0) {
            this.driveFolderId = response.result.files[0].id;
            console.log(`Found existing folder with ID: ${this.driveFolderId}`);
        } else {
            console.log(`Creating new folder: "${FOLDER_NAME}"`);
            const fileMetadata = { name: FOLDER_NAME, mimeType: 'application/vnd.google-apps.folder' };
            const createResponse = await gapi.client.drive.files.create({ resource: fileMetadata, fields: 'id' });
            this.driveFolderId = createResponse.result.id;
            console.log(`Created new folder with ID: ${this.driveFolderId}`);
        }
        return this.driveFolderId!;
    }

    public async getSettingsFromDrive(): Promise<AppSettings | null> {
        await this.findSettingsFileId();
        if (!this.settingsFileId) return null;

        try {
            const gapi = (window as any).gapi;
            const response = await gapi.client.drive.files.get({ fileId: this.settingsFileId, alt: 'media' });
            return JSON.parse(response.body) as AppSettings;
        } catch (error) {
            console.error('Error fetching settings from Drive:', error);
            return null;
        }
    }

    public async saveSettingsToDrive(settings: AppSettings): Promise<void> {
        const folderId = await this.getOrCreateDriveFolder();
        await this.findSettingsFileId();
        const settingsBlob = new Blob([JSON.stringify(settings, null, 2)], { type: 'application/json' });
        
        const gapi = (window as any).gapi;
        const form = new FormData();

        if (this.settingsFileId) { // Update existing file
            form.append('metadata', new Blob([JSON.stringify({ mimeType: 'application/json' })], { type: 'application/json' }));
            form.append('file', settingsBlob);

            await fetch(`https://www.googleapis.com/upload/drive/v3/files/${this.settingsFileId}?uploadType=multipart`, {
                method: 'PATCH',
                headers: new Headers({ 'Authorization': `Bearer ${gapi.client.getToken().access_token}` }),
                body: form,
            });
        } else { // Create new file
            const metadata = { name: SETTINGS_FILE_NAME, parents: [folderId], mimeType: 'application/json' };
            form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
            form.append('file', settingsBlob);

            const response = await fetch(`https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart`, {
                method: 'POST',
                headers: new Headers({ 'Authorization': `Bearer ${gapi.client.getToken().access_token}` }),
                body: form,
            });
            const file = await response.json();
            this.settingsFileId = file.id;
        }
    }

    private async findSettingsFileId(): Promise<void> {
        if (this.settingsFileId) return;
        const folderId = await this.getOrCreateDriveFolder();
        const gapi = (window as any).gapi;
        
        const response = await gapi.client.drive.files.list({
            q: `name='${SETTINGS_FILE_NAME}' and '${folderId}' in parents and trashed=false`,
            fields: 'files(id)',
            spaces: 'drive',
        });

        if (response.result.files && response.result.files.length > 0) {
            this.settingsFileId = response.result.files[0].id;
        }
    }
    
    public async getNoteRevisions(fileId: string): Promise<any[]> {
        const gapi = (window as any).gapi;
        try {
            const response = await gapi.client.drive.revisions.list({
                fileId,
                fields: 'revisions(id, modifiedTime, lastModifyingUser/displayName, lastModifyingUser/photoLink)',
            });
            return response.result.revisions?.reverse() || [];
        } catch (error) {
            console.error('Error fetching revisions:', error);
            return [];
        }
    }

    public async getRevisionContent(fileId: string, revisionId: string): Promise<string> {
        const gapi = (window as any).gapi;
        try {
            const response = await gapi.client.drive.revisions.get({
                fileId,
                revisionId,
                alt: 'media',
            });
            return response.body;
        } catch (error) {
            console.error('Error fetching revision content:', error);
            throw new Error('Could not fetch revision content.');
        }
    }

    public async deleteNoteFromDrive(fileId: string): Promise<void> {
        const gapi = (window as any).gapi;
        try {
            await gapi.client.drive.files.update({
                fileId: fileId,
                resource: { trashed: true },
            });
        } catch (error) {
            console.error('Error deleting note from Drive:', error);
            throw new Error('Failed to delete note from Google Drive.');
        }
    }
}